import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

export interface FilterContentProps {
  title: string;
  options: string[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  onClose: () => void;
}

export const FilterContent: React.FC<FilterContentProps> = React.memo(({
  title,
  options,
  selectedValues,
  onSelectionChange,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => 
    options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [options, searchTerm]
  );

  const handleToggleOption = useCallback((option: string) => {
    const newSelection = selectedValues.includes(option)
      ? selectedValues.filter(v => v !== option)
      : [...selectedValues, option];
    onSelectionChange(newSelection);
  }, [selectedValues, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(filteredOptions);
  }, [filteredOptions, onSelectionChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  return (
    <Box sx={{ minWidth: 280, maxWidth: 320 }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Filter {title}
        </Typography>
        {selectedValues.length > 0 && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
          >
            Clear
          </Button>
        )}
      </Box>

      <Divider />

      {/* Search */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Box>

      <Divider />

      {/* Select All/None */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          onClick={handleSelectAll}
          sx={{ fontSize: '0.75rem', minWidth: 'auto', textTransform: 'none' }}
        >
          Select All
        </Button>
        <Typography variant="caption" color="text.disabled">|</Typography>
        <Button
          size="small"
          onClick={handleClearAll}
          sx={{ fontSize: '0.75rem', minWidth: 'auto', textTransform: 'none' }}
        >
          Select None
        </Button>
      </Box>

      <Divider />

      {/* Options List */}
      <List sx={{ maxHeight: 240, overflow: 'auto', py: 0 }}>
        {filteredOptions.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No options found"
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ) : (
          filteredOptions.map((option) => {
            const isChecked = selectedValues.includes(option);
            return (
              <ListItemButton
                key={option}
                dense
                onClick={() => handleToggleOption(option)}
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={option || '(Empty)'}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    title: option,
                  }}
                />
              </ListItemButton>
            );
          })
        )}
      </List>

      {/* Footer with count */}
      {filteredOptions.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {selectedValues.length} of {options.length} selected
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
});

FilterContent.displayName = 'FilterContent';
